// The portal's server state, as TanStack Query hooks (spec/03 §3). One key per resource, here, so a
// sign-out or a write can invalidate exactly what it changed.
//
// Everything a signed-in screen shows is the player's own, read from `/v1/me/*` (ADR-0054): wallets and
// statements, passes, play history and the live session, orders with receipts, and the passes the player can buy at a
// venue from their wallet there; and a venue's page reads its public listing and its shop (`/v1/public/venues/{slug}`,
// `/shop`). The live session is polled while a screen that shows it is open, and counted down locally against the
// server's clock in between.

import type {
  AuthSessionState,
  LinkedAccount,
  MyConsents,
  MyCurrentPlaySession,
  MyOrder,
  MyOrderList,
  MyPayment,
  MyPassList,
  MyPlaySessionList,
  MyProfile,
  MyVenuePassShop,
  MyWalletList,
  MyWalletStatement,
  PublicVenue,
  PublicVenueShop,
  SignedInSession,
  SocialProvider,
} from '@clavian/api-client';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { useApi } from './client';
import { problemStatus } from './problems';

export const queryKeys = {
  session: ['session'] as const,
  mySessions: ['me', 'sessions'] as const,
  linkedAccounts: ['me', 'linked-accounts'] as const,
  me: ['me', 'profile'] as const,
  consents: ['me', 'consents'] as const,
  notificationPrefs: ['me', 'notification-prefs'] as const,
  meta: ['meta'] as const,
  wallets: ['me', 'wallets'] as const,
  statement: (organizationId: string) => ['me', 'wallets', organizationId, 'statement'] as const,
  passes: ['me', 'passes'] as const,
  playSessions: ['me', 'play-sessions'] as const,
  live: ['me', 'play-sessions', 'current'] as const,
  orders: ['me', 'orders'] as const,
  order: (saleId: string) => ['me', 'orders', saleId] as const,
  payment: (paymentId: string) => ['me', 'payments', paymentId] as const,
  myVenueShop: (venueId: string) => ['me', 'venues', venueId, 'passes'] as const,
  publicVenues: (filter: Readonly<Record<string, string>>) => ['public', 'venues', filter] as const,
  publicVenue: (slug: string) => ['public', 'venue', slug] as const,
  venueShop: (slug: string) => ['public', 'venue', slug, 'shop'] as const,
};

/** How often a screen showing the live session asks again; it counts down locally in between. */
export const LIVE_POLL_MS = 10_000;
/** How often the live session is read while a lock or unlock asked from the phone waits for the PC. */
export const LOCK_POLL_MS = 2_000;

/**
 * Whether a phone and a code sign anyone in here (`GET /v1/meta`'s `signIn.phone`, the Platform's `auth.phoneSignIn`;
 * off since the owner's instruction of 2026-10-08). Read defensively: an answer without it, or no answer yet, is
 * off, so the phone path appears only where the server says, in so many words, that it is on.
 */
export function phoneSignInOf(meta: unknown): boolean {
  return (meta as { signIn?: { phone?: unknown } } | null | undefined)?.signIn?.phone === true;
}

/** The public meta (`GET /v1/meta`), read as `unknown` and taken apart defensively by the helpers below. */
function useMeta() {
  const api = useApi();
  return useQuery<unknown>({ queryKey: queryKeys.meta, queryFn: () => api.getMeta(), staleTime: 60_000 });
}

export function usePhoneSignIn(): boolean {
  return phoneSignInOf(useMeta().data);
}

const KNOWN_PROVIDERS: readonly SocialProvider[] = ['google', 'discord', 'steam'];

/** The social providers this deployment has switched on (`GET /v1/meta`'s `signIn.social`), in button order. */
export function socialProvidersOf(meta: unknown): readonly SocialProvider[] {
  const social: unknown = (meta as { signIn?: { social?: unknown } } | null | undefined)?.signIn?.social;
  if (!Array.isArray(social)) return [];
  return KNOWN_PROVIDERS.filter((provider) => social.includes(provider));
}

export function useSocialProviders(): { readonly providers: readonly SocialProvider[]; readonly isPending: boolean } {
  const meta = useMeta();
  return { providers: socialProvidersOf(meta.data), isPending: meta.isPending };
}

/** A legal document as `GET /v1/meta` names it: the version a sign-up must send, and where to read it. */
export interface LegalDocument {
  readonly version: string;
  readonly url: string;
}

/** The terms and the privacy policy in force, or `undefined` while the brand names neither (spec/03 §12.4). */
export interface BrandLegal {
  readonly terms: LegalDocument;
  readonly privacy: LegalDocument;
}

function legalDocument(value: unknown): LegalDocument | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const { version, url } = value as Record<string, unknown>;
  return typeof version === 'string' && typeof url === 'string' ? { version, url } : undefined;
}

/** `brand.legal` of the public meta, read defensively: a sign-up offers nothing to agree to without both. */
export function legalOf(meta: unknown): BrandLegal | undefined {
  const legal: unknown = (meta as { brand?: { legal?: unknown } } | null | undefined)?.brand?.legal;
  if (typeof legal !== 'object' || legal === null) return undefined;
  const terms = legalDocument((legal as Record<string, unknown>)['terms']);
  const privacy = legalDocument((legal as Record<string, unknown>)['privacy']);
  return terms === undefined || privacy === undefined ? undefined : { terms, privacy };
}

export function useBrandLegal(): { readonly legal: BrandLegal | undefined; readonly isPending: boolean } {
  const meta = useMeta();
  return { legal: legalOf(meta.data), isPending: meta.isPending };
}

/** The caller's own profile (`GET /v1/me`), with whether it still needs completing after a social sign-up. */
export function useMe(enabled = true) {
  const api = useApi();
  return useQuery<MyProfile>({ queryKey: queryKeys.me, queryFn: () => api.getMe(), enabled, retry: false });
}

/** What the player agreed to (`GET /v1/me/consents`), to ask again when the terms in force changed. */
export function useConsents(enabled = true) {
  const api = useApi();
  return useQuery<MyConsents>({ queryKey: queryKeys.consents, queryFn: () => api.getMyConsents(), enabled, retry: false, staleTime: 300_000 });
}

/** The ways into the caller's account (`GET /v1/auth/list-accounts`): the password and every linked provider. */
export function useLinkedAccounts(enabled = true) {
  const api = useApi();
  return useQuery<readonly LinkedAccount[]>({
    queryKey: queryKeys.linkedAccounts,
    queryFn: async () => {
      const body: unknown = await api.listUserAccounts();
      return Array.isArray(body) ? (body as LinkedAccount[]) : [];
    },
    enabled,
    retry: false,
  });
}

/** The better-auth session, or `null` when signed out. */
export function useSession() {
  const api = useApi();
  return useQuery<AuthSessionState>({ queryKey: queryKeys.session, queryFn: () => api.getSession(), staleTime: 60_000 });
}

export interface PublicVenuesFilter {
  readonly openNow?: boolean;
}

/** The public venue directory (`GET /v1/public/venues`), unauthenticated, up to 100 venues. */
export function usePublicVenues(filter: PublicVenuesFilter = {}) {
  const api = useApi();
  const open = filter.openNow === true;
  return useQuery<readonly PublicVenue[]>({
    queryKey: queryKeys.publicVenues(open ? { openNow: 'true' } : {}),
    queryFn: async () => {
      const body = await api.listPublicVenues({ query: { limit: 100, ...(open ? { openNow: 'true' as const } : {}) } });
      return Array.isArray(body.data) ? body.data : [];
    },
  });
}

/** One venue's public page (`GET /v1/public/venues/{slug}`). A venue not listed or not live is 404. */
export function usePublicVenue(slug: string) {
  const api = useApi();
  return useQuery<PublicVenue>({
    queryKey: queryKeys.publicVenue(slug),
    queryFn: () => api.getPublicVenue({ path: { slug } }),
    retry: false,
  });
}

/** What a venue sells and how many PCs are free (`GET /v1/public/venues/{slug}/shop`). */
export function useVenueShop(slug: string, enabled = true) {
  const api = useApi();
  return useQuery<PublicVenueShop>({
    queryKey: queryKeys.venueShop(slug),
    queryFn: () => api.getPublicVenueShop({ path: { slug } }),
    enabled,
    retry: false,
    refetchInterval: 60_000,
  });
}

/**
 * The passes the player can buy at a venue from their wallet there, and that wallet (`GET /v1/me/venues/{venueId}/passes`).
 * A venue at no café the player has dealt with is 404: they hold no wallet there.
 */
export function useMyVenuePassShop(venueId: string | undefined, enabled = true) {
  const api = useApi();
  return useQuery<MyVenuePassShop>({
    queryKey: queryKeys.myVenueShop(venueId ?? ''),
    queryFn: () => api.getMyVenuePassShop({ path: { venueId: venueId ?? '' } }),
    enabled: enabled && venueId !== undefined,
    retry: false,
  });
}

/** The caller's signed-in sessions, one per device (`GET /v1/me/sessions`). */
export function useMySessions(enabled = true) {
  const api = useApi();
  return useQuery<readonly SignedInSession[]>({
    queryKey: queryKeys.mySessions,
    queryFn: async () => {
      const body = await api.listMySessions();
      return Array.isArray(body.data) ? body.data : [];
    },
    enabled,
    retry: false,
  });
}

/** The player's wallets, one per organisation (`GET /v1/me/wallets`). */
export function useWallets() {
  const api = useApi();
  return useQuery<MyWalletList>({ queryKey: queryKeys.wallets, queryFn: () => api.listMyWallets() });
}

/** One wallet's statement, newest first, a page at a time. */
export function useStatement(organizationId: string | undefined) {
  const api = useApi();
  return useInfiniteQuery<MyWalletStatement>({
    queryKey: queryKeys.statement(organizationId ?? ''),
    enabled: organizationId !== undefined,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.getMyWalletStatement({
        path: { organizationId: organizationId ?? '' },
        query: { limit: 20, ...(typeof pageParam === 'string' ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => (last.page.hasMore ? (last.page.cursor ?? undefined) : undefined),
  });
}

/** The player's passes: active ones, then the recent past (`GET /v1/me/passes`). */
export function usePasses() {
  const api = useApi();
  return useQuery<MyPassList>({ queryKey: queryKeys.passes, queryFn: () => api.listMyPasses() });
}

/** The live session, polled while a screen shows it (`GET /v1/me/play-sessions/current`). */
export function useLiveSession(poll = true) {
  const api = useApi();
  return useQuery<MyCurrentPlaySession>({
    queryKey: queryKeys.live,
    queryFn: () => api.getMyCurrentPlaySession(),
    // While the PC is still to lock or unlock as asked, read again sooner: the PC answers within seconds.
    refetchInterval: (query) => (!poll ? false : query.state.data?.live?.lockRequest?.state === 'pending' ? LOCK_POLL_MS : LIVE_POLL_MS),
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  });
}

/** Play history, newest first, a page at a time (`GET /v1/me/play-sessions`). */
export function usePlayHistory(pageSize = 10) {
  const api = useApi();
  return useInfiniteQuery<MyPlaySessionList>({
    queryKey: [...queryKeys.playSessions, pageSize],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.listMyPlaySessions({ query: { limit: pageSize, ...(typeof pageParam === 'string' ? { cursor: pageParam } : {}) } }),
    getNextPageParam: (last) => (last.page.hasMore ? (last.page.cursor ?? undefined) : undefined),
  });
}

/** Orders, newest first, a page at a time (`GET /v1/me/orders`). */
export function useOrders(pageSize = 15) {
  const api = useApi();
  return useInfiniteQuery<MyOrderList>({
    queryKey: [...queryKeys.orders, pageSize],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.listMyOrders({ query: { limit: pageSize, ...(typeof pageParam === 'string' ? { cursor: pageParam } : {}) } }),
    getNextPageParam: (last) => (last.page.hasMore ? (last.page.cursor ?? undefined) : undefined),
  });
}

/** One order with its receipt (`GET /v1/me/orders/{saleId}`). */
export function useOrder(saleId: string) {
  const api = useApi();
  return useQuery<MyOrder>({
    queryKey: queryKeys.order(saleId),
    queryFn: () => api.getMyOrder({ path: { saleId: saleId } }),
    retry: false,
  });
}

/** How often a payment's state is asked while its page waits for the provider to confirm. */
export const PAYMENT_POLL_MS = 2_000;
/** How long the portal keeps asking before it says the confirmation is slow. */
export const PAYMENT_GIVE_UP_MS = 180_000;

/** One of the player's online payments (`GET /v1/me/payments/{paymentIntentId}`), asked again until it is settled. */
export function useMyPayment(paymentId: string | undefined, enabled: boolean) {
  const api = useApi();
  return useQuery<MyPayment>({
    queryKey: queryKeys.payment(paymentId ?? ''),
    queryFn: () => api.getMyPayment({ path: { paymentIntentId: paymentId ?? '' } }),
    enabled: enabled && paymentId !== undefined,
    retry: false,
    gcTime: 0,
    refetchInterval: (query) => {
      if (!enabled) return false;
      // Someone else's payment, or none: asking again will not change the answer.
      const refused = problemStatus(query.state.error);
      if (refused === 404 || refused === 401) return false;
      return query.state.data === undefined || query.state.data.status === 'pending' ? PAYMENT_POLL_MS : false;
    },
  });
}
